import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Trash2, MapPin, FileDown, Lock,
  CreditCard, Phone, Smartphone, ChevronRight,
  CheckCircle, AlertCircle, ArrowLeft, Loader,
} from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { paymentsApi } from '../api/client';

const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY || '';

const METHODS = [
  { id: 'stripe', label: 'Carte bancaire',  sub: 'Visa / Mastercard',   icon: CreditCard,  color: '#818cf8' },
  { id: 'mvola',  label: 'MVola',           sub: 'Telma Madagascar',     icon: Phone,       color: '#e11d48' },
  { id: 'orange', label: 'Orange Money',    sub: 'Orange Madagascar',    icon: Smartphone,  color: '#f97316' },
  { id: 'airtel', label: 'Airtel Money',    sub: 'Airtel Madagascar',    icon: Smartphone,  color: '#dc2626' },
];

/* ── Carte bancaire décorative avec flip ── */
function CardVisual({ totalMga, itemCount }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div>
      <style>{`
        .cv-scene { width: 100%; height: 160px; perspective: 900px; cursor: pointer; }
        .cv-3d {
          width: 100%; height: 100%; position: relative;
          transform-style: preserve-3d;
          transition: transform 0.5s cubic-bezier(.4,0,.2,1);
        }
        .cv-3d.flipped { transform: rotateY(180deg); }
        .cv-face {
          position: absolute; inset: 0; border-radius: 13px;
          backface-visibility: hidden; -webkit-backface-visibility: hidden;
          padding: 16px 20px; box-sizing: border-box;
          display: flex; flex-direction: column; justify-content: space-between;
        }
        .cv-front {
          background: linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%);
          border: 1px solid rgba(99,102,241,0.45);
          box-shadow: 0 12px 40px rgba(79,70,229,0.3);
        }
        .cv-back {
          background: linear-gradient(135deg, #1e1b4b 0%, #1e293b 100%);
          border: 1px solid rgba(99,102,241,0.3);
          transform: rotateY(180deg);
        }
        .cv-chip { width: 32px; height: 24px; border-radius: 4px; background: linear-gradient(135deg,#d4a840,#f5c842,#b8922c); }
        .cv-mag { height: 32px; background: #0a0f1e; margin: 0 -20px; }
        .cv-sig {
          background: repeating-linear-gradient(90deg,#f1f5f9 0,#f1f5f9 18px,#e2e8f0 18px,#e2e8f0 22px);
          height: 28px; border-radius: 3px; display: flex; align-items: center; padding: 0 8px; gap: 8px;
        }
        .cv-num { letter-spacing: 2.5px; font-size: 14px; color: rgba(255,255,255,0.9); font-weight: 600; font-family: 'Courier New', monospace; }
        .cv-lbl { font-size: 8px; color: rgba(255,255,255,0.4); letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 1px; }
        .cv-val { font-size: 11px; color: rgba(255,255,255,0.85); letter-spacing: 0.5px; font-family: 'Courier New', monospace; }
      `}</style>

      <div className="cv-scene" onClick={() => setFlipped(f => !f)} title="Cliquer pour voir le CVC">
        <div className={`cv-3d${flipped ? ' flipped' : ''}`}>
          <div className="cv-face cv-front">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="cv-chip" />
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', letterSpacing: 1 }}>MODE TEST</div>
                <div style={{ fontSize: 10, color: 'rgba(165,180,252,0.8)', fontWeight: 700 }}>STRIPE</div>
              </div>
            </div>
            <div className="cv-num">4242  4242  4242  4242</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div>
                <div className="cv-lbl">Titulaire</div>
                <div className="cv-val">MADJEO TEST</div>
              </div>
              <div>
                <div className="cv-lbl">Expire</div>
                <div className="cv-val">12/34</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="cv-lbl">{itemCount} pt{itemCount > 1 ? 's' : ''}</div>
                <div style={{ fontSize: 12, color: '#86efac', fontWeight: 700, fontFamily: 'system-ui' }}>{totalMga} MGA</div>
              </div>
            </div>
          </div>

          <div className="cv-face cv-back">
            <div className="cv-mag" />
            <div>
              <div className="cv-sig">
                <div style={{ flex: 1, fontSize: 9, color: '#64748b', letterSpacing: 2 }}>▓▓▓▓▓▓▓▓▓▓▓▓▓</div>
                <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 3, padding: '3px 8px', fontSize: 13, fontWeight: 700, color: '#1e293b', letterSpacing: 2 }}>123</div>
              </div>
              <div style={{ fontSize: 8, color: '#475569', marginTop: 4, letterSpacing: 0.5 }}>CVC — 3 chiffres au dos</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Lock size={9} style={{ color: '#4f46e5' }} />
              <span style={{ fontSize: 9, color: '#475569' }}>256-bit SSL · Stripe Inc.</span>
            </div>
          </div>
        </div>
      </div>
      <div style={{ textAlign: 'center', fontSize: 10, color: '#334155', marginTop: 6 }}>↩ Cliquer pour voir le CVC</div>
    </div>
  );
}

/* ── Formulaire Stripe ── */
function StripeForm({ onPaymentMethod, disabled, totalMga, totalEur, itemCount }) {
  const mountRef    = useRef(null);
  const stripeRef   = useRef(null);
  const elementsRef = useRef(null);
  const [ready,     setReady]   = useState(false);
  const [loading,   setLoading] = useState(false);
  const [error,     setError]   = useState(null);
  const [copied,    setCopied]  = useState(false);

  useEffect(() => {
    if (!mountRef.current) return;
    if (!window.Stripe || !STRIPE_PUBLIC_KEY) {
      setError('Clé Stripe non configurée (REACT_APP_STRIPE_PUBLIC_KEY)');
      return;
    }
    const stripe   = window.Stripe(STRIPE_PUBLIC_KEY);
    const elements = stripe.elements({
      mode: 'payment',
      paymentMethodCreation: 'manual',
      amount: Math.max(Math.round((totalEur || 0.5) * 100), 50),
      currency: 'eur',
      locale: 'fr',
      appearance: {
        theme: 'night',
        variables: {
          colorPrimary:          '#6366f1',
          colorBackground:       '#0f172a',
          colorText:             '#e2e8f0',
          colorTextSecondary:    '#94a3b8',
          colorTextPlaceholder:  '#475569',
          colorDanger:           '#f87171',
          colorInputBackground:  '#1e293b',
          colorInputText:        '#f1f5f9',
          colorInputBorder:      '#334155',
          fontFamily:            'system-ui, -apple-system, sans-serif',
          fontSizeBase:          '13px',
          borderRadius:          '8px',
          spacingUnit:           '4px',
        },
        rules: {
          '.Input':        { border: '1px solid #334155', boxShadow: 'none' },
          '.Input:focus':  { borderColor: '#6366f1', boxShadow: '0 0 0 3px rgba(99,102,241,0.2)' },
          '.Label':        { color: '#94a3b8', fontSize: '11px', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' },
          '.Tab':          { border: '1px solid #1e293b', background: '#0f1829', color: '#64748b' },
          '.Tab:hover':    { background: '#131f35', color: '#94a3b8' },
          '.Tab--selected':{ border: '1px solid #6366f1', background: 'rgba(99,102,241,0.08)', color: '#818cf8' },
          '.TabLabel--selected': { color: '#818cf8' },
          '.Error':        { color: '#f87171', fontSize: '12px' },
        },
      },
    });
    stripeRef.current   = stripe;
    elementsRef.current = elements;
    const payEl = elements.create('payment', {
      layout:  { type: 'tabs', defaultCollapsed: false },
      wallets: { applePay: 'never', googlePay: 'never' },
    });
    payEl.mount(mountRef.current);
    payEl.on('ready',  () => setReady(true));
    payEl.on('change', e => { if (e.complete) setError(null); });
    return () => { try { payEl.unmount(); } catch {} };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!stripeRef.current || !elementsRef.current) return;
    setLoading(true); setError(null);
    try {
      const { error: e1 } = await elementsRef.current.submit();
      if (e1) { setError(e1.message); return; }
      const { error: e2, paymentMethod } = await stripeRef.current.createPaymentMethod({ elements: elementsRef.current });
      if (e2) { setError(e2.message); return; }
      onPaymentMethod(stripeRef.current, paymentMethod);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const copyCard = () => {
    navigator.clipboard?.writeText('4242424242424242').then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Carte visuelle */}
      <CardVisual totalMga={totalMga} itemCount={itemCount} />

      {/* Chip test + copier */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.2)',
        borderRadius: 8, padding: '8px 12px',
      }}>
        <div style={{ background: 'rgba(99,102,241,0.2)', borderRadius: 4, padding: '2px 6px', fontSize: 9, fontWeight: 700, color: '#818cf8', flexShrink: 0 }}>TEST</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#c7d2fe', letterSpacing: 1.5, fontWeight: 600 }}>
            4242 4242 4242 4242
          </div>
          <div style={{ fontSize: 10, color: '#475569', marginTop: 1 }}>
            exp&nbsp;<strong style={{ color: '#94a3b8' }}>12/34</strong>&nbsp;·&nbsp;cvc&nbsp;<strong style={{ color: '#94a3b8' }}>123</strong>
          </div>
        </div>
        <button onClick={copyCard} style={{
          background: copied ? 'rgba(34,197,94,0.12)' : 'rgba(99,102,241,0.12)',
          border: `1px solid ${copied ? 'rgba(34,197,94,0.3)' : 'rgba(99,102,241,0.3)'}`,
          borderRadius: 5, padding: '4px 9px', cursor: 'pointer',
          fontSize: 10, color: copied ? '#86efac' : '#818cf8', fontFamily: 'inherit', flexShrink: 0,
        }}>
          {copied ? '✓ Copié' : 'Copier'}
        </button>
      </div>

      {/* Séparateur */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, height: 1, background: 'rgba(var(--ds-indigo-rgb),0.14)' }} />
        <span style={{ fontSize: 10, color: 'var(--c-dim)', letterSpacing: 0.5, flexShrink: 0 }}>COORDONNÉES BANCAIRES</span>
        <div style={{ flex: 1, height: 1, background: 'rgba(var(--ds-indigo-rgb),0.14)' }} />
      </div>

      {/* Erreur */}
      {error && (
        <div className="pan-alert pan-alert--error"><AlertCircle size={13} />{error}</div>
      )}

      {/* Payment Element */}
      <div style={{ position: 'relative' }}>
        {!ready && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            background: 'var(--c-bg)', borderRadius: 8, color: 'var(--c-muted)', fontSize: 12,
          }}>
            <Loader size={13} style={{ animation: 'spin 0.65s linear infinite' }} />
            Chargement du formulaire…
          </div>
        )}
        <div ref={mountRef} style={{ minHeight: 180 }} />
      </div>

      {/* Bouton */}
      {ready && (
        <button
          className="pan-btn pan-btn--primary"
          style={{
            width: '100%', padding: '11px 16px', fontSize: 13,
            background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
            boxShadow: '0 4px 16px rgba(99,102,241,0.35)',
          }}
          onClick={handleSubmit}
          disabled={disabled || loading}
        >
          {loading
            ? <><Loader size={14} style={{ animation: 'spin 0.65s linear infinite' }} /> Validation…</>
            : <><Lock size={13} /> Payer {totalMga} MGA · {totalEur?.toFixed(2)} EUR</>
          }
        </button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
        <Lock size={9} style={{ color: 'var(--c-dim)' }} />
        <span style={{ fontSize: 10, color: 'var(--c-dim)' }}>Paiement sécurisé SSL · Stripe · Aucune donnée stockée</span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
export default function Panier() {
  const navigate = useNavigate();
  const { items, removeFromCart, clearCart } = useCart();

  const [step,     setStep]    = useState('cart');
  const [method,   setMethod]  = useState(null);
  const [phone,    setPhone]   = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0, results: [] });

  const totalMga = items.reduce((s, i) => s + Number(i.amount_mga || 0), 0);
  const totalEur = items.reduce((s, i) => s + Number(i.amount_eur || 0), 0);

  /* ── Polling générique (MVola, Airtel, Orange) — max 3 min ── */
  const pollStatus = useCallback(async (purchaseId, providerMethod) => {
    const INTERVAL = 4000;
    const MAX_MS   = 180_000;
    const start    = Date.now();

    const fetchStatus = async () => {
      if (providerMethod === 'mvola')  return paymentsApi.mvolaStatus(purchaseId);
      if (providerMethod === 'airtel') return paymentsApi.airtelStatus(purchaseId);
      if (providerMethod === 'orange') return paymentsApi.orangeStatus(purchaseId, `MADJEO-${purchaseId}`);
      throw new Error('Méthode inconnue');
    };

    const labels = { mvola: 'MVola', airtel: 'Airtel', orange: 'Orange Money' };

    while (Date.now() - start < MAX_MS) {
      await new Promise(r => setTimeout(r, INTERVAL));
      try {
        const res = await fetchStatus();
        const s   = res.data.status;
        if (s === 'completed') return { ok: true };
        if (s === 'failed')    return { ok: false, err: `Paiement refusé par ${labels[providerMethod]}` };
      } catch { /* on réessaie */ }
    }
    return { ok: false, err: 'Délai dépassé (3 min) — vérifiez votre téléphone' };
  }, []);

  /* ── Traitement mobile money (MVola / Airtel / Orange) ── */
  const processMomo = useCallback(async () => {
    if (!phone.trim() && method !== 'orange') return;
    setStep('pay');
    setProgress({ current: 0, total: items.length, results: [] });
    const results = [];

    for (let i = 0; i < items.length; i++) {
      setProgress(p => ({ ...p, current: i + 1 }));
      const item = items[i];
      try {
        const init       = await paymentsApi.initiate({ point_id: item.id, payment_method: method });
        const purchaseId = init.data.purchase_id;

        if (method === 'mvola') {
          await paymentsApi.mvolaInitiate(purchaseId, phone.trim());
          const res = await pollStatus(purchaseId, 'mvola');
          if (!res.ok) throw new Error(res.err);

        } else if (method === 'airtel') {
          await paymentsApi.airtelInitiate(purchaseId, phone.trim());
          const res = await pollStatus(purchaseId, 'airtel');
          if (!res.ok) throw new Error(res.err);

        } else if (method === 'orange') {
          const initRes   = await paymentsApi.orangeInitiate(purchaseId);
          const paymentUrl = initRes.data.payment_url;
          const testMode   = initRes.data.test_mode;

          if (!testMode && paymentUrl) {
            window.open(paymentUrl, '_blank', 'noopener,noreferrer');
          }
          const res = await pollStatus(purchaseId, 'orange');
          if (!res.ok) throw new Error(res.err);
        }

        results.push({ id: item.id, nom: item.nom, ok: true });
      } catch (e) {
        results.push({ id: item.id, nom: item.nom, ok: false, err: e.response?.data?.error || e.message || 'Erreur' });
      }
    }

    setProgress(p => ({ ...p, results }));
    if (results.every(r => r.ok)) clearCart();
    setStep('done');
  }, [items, method, phone, clearCart, pollStatus]);

  /* ── Traitement Stripe ── */
  const processStripe = useCallback(async (stripe, pm) => {
    setStep('pay');
    setProgress({ current: 0, total: items.length, results: [] });
    const results = [];
    for (let i = 0; i < items.length; i++) {
      setProgress(p => ({ ...p, current: i + 1 }));
      try {
        const init   = await paymentsApi.initiate({ point_id: items[i].id, payment_method: 'stripe' });
        const intent = await paymentsApi.stripeIntent({ purchase_id: init.data.purchase_id });
        const { error } = await stripe.confirmCardPayment(intent.data.client_secret, { payment_method: pm.id });
        if (error) throw new Error(error.message);
        await paymentsApi.stripeConfirm(init.data.purchase_id);
        results.push({ id: items[i].id, nom: items[i].nom, ok: true });
      } catch (e) {
        results.push({ id: items[i].id, nom: items[i].nom, ok: false, err: e.message || 'Erreur' });
      }
    }
    setProgress(p => ({ ...p, results }));
    if (results.every(r => r.ok)) clearCart();
    setStep('done');
  }, [items, clearCart]);

  /* ── Styles communs (thème-aware via variables CSS) ── */
  const css = `
    @keyframes spin { to { transform: rotate(360deg); } }
    .pan-btn {
      display: inline-flex; align-items: center; justify-content: center;
      gap: 7px; padding: 9px 16px; border-radius: 7px; border: none;
      cursor: pointer; font-size: 13px; font-weight: 600;
      font-family: inherit; transition: opacity .12s, background .12s;
    }
    .pan-btn:disabled { opacity: 0.45; cursor: not-allowed; }
    .pan-btn--primary { background: #4f46e5; color: #fff; }
    .pan-btn--primary:hover:not(:disabled) { background: #4338ca; }
    .pan-btn--ghost {
      background: rgba(var(--ds-panel-rgb), 0.7);
      color: var(--c-muted);
      border: 1px solid rgba(var(--ds-indigo-rgb), 0.15);
    }
    .pan-btn--ghost:hover:not(:disabled) {
      background: rgba(var(--ds-panel-rgb), 1);
      color: var(--c-text);
    }
    .pan-btn--danger { background: rgba(239,68,68,0.08); color: #f87171; border: 1px solid rgba(239,68,68,0.2); }
    .pan-btn--danger:hover:not(:disabled) { background: rgba(239,68,68,0.15); }
    .pan-input {
      width: 100%;
      background: rgba(var(--ds-input-rgb), 0.8);
      border: 1px solid rgba(var(--ds-indigo-rgb), 0.15);
      border-radius: 7px; padding: 10px 14px;
      color: var(--c-text);
      font-family: inherit; font-size: 13px; outline: none; box-sizing: border-box;
    }
    .pan-input:focus { border-color: #4f46e5; }
    .pan-input::placeholder { color: var(--ds-text-ph); }
    .pan-alert { display: flex; align-items: center; gap: 7px; padding: 10px 14px; border-radius: 8px; font-size: 12px; }
    .pan-alert--error { background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.25); color: #fca5a5; }
    .pan-method-card {
      display: flex; align-items: center; gap: 12px;
      padding: 11px 14px; border-radius: 9px; cursor: pointer;
      border: 1.5px solid rgba(var(--ds-indigo-rgb), 0.14);
      background: rgba(var(--ds-panel-rgb), 0.8);
      transition: border-color .15s, background .15s;
      margin-bottom: 8px; width: 100%; text-align: left; font-family: inherit;
    }
    .pan-method-card:hover {
      background: rgba(var(--ds-panel-rgb), 1);
      border-color: rgba(var(--ds-indigo-rgb), 0.28);
    }
    .pan-method-card--active {
      border-color: #4f46e5;
      background: rgba(79,70,229,0.07);
    }
    .pan-item-card {
      display: flex; align-items: center; gap: 12px;
      padding: 11px 14px; border-radius: 9px; margin-bottom: 8px;
      background: rgba(var(--ds-panel-rgb), 0.8);
      border: 1px solid rgba(var(--ds-indigo-rgb), 0.14);
    }
  `;

  return (
    <div style={{ padding: '24px 24px 48px', color: 'var(--c-text)', fontFamily: 'inherit' }}>
      <style>{css}</style>

      {/* ── En-tête ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        {step !== 'done' && (
          <button className="pan-btn pan-btn--ghost" style={{ padding: '7px 10px', flexShrink: 0 }}
            onClick={() => { if (step === 'cart') navigate(-1); else if (step === 'method') setStep('cart'); }}>
            <ArrowLeft size={15} />
          </button>
        )}
        <div style={{
          width: 38, height: 38, borderRadius: 9, flexShrink: 0,
          background: 'rgba(79,70,229,0.12)', border: '1px solid rgba(79,70,229,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ShoppingCart size={17} style={{ color: 'var(--cyan-500)' }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--c-text)', lineHeight: 1.2 }}>
            {step === 'done' ? 'Commande traitée' : 'Mon Panier'}
          </h1>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--c-muted)', marginTop: 1 }}>
            {step === 'cart'   && `${items.length} point${items.length > 1 ? 's' : ''} — accès Laborde + PDF`}
            {step === 'method' && 'Mode de paiement'}
            {step === 'pay'    && (() => {
              const labels = { mvola: 'MVola', airtel: 'Airtel Money', orange: 'Orange Money' };
              const lbl    = labels[method] || '';
              return lbl
                ? `Validation ${lbl} … ${progress.current} / ${progress.total}`
                : `Traitement ${progress.current} / ${progress.total}…`;
            })()}
            {step === 'done'   && 'Résumé des paiements'}
          </p>
        </div>
      </div>

      {/* ══ ÉTAPE : PANIER ══ */}
      {step === 'cart' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 20, alignItems: 'start' }}>
          <div>
            {items.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 20px', background: 'rgba(var(--ds-panel-rgb),0.8)', border: '1px solid rgba(var(--ds-indigo-rgb),0.14)', borderRadius: 12 }}>
                <ShoppingCart size={32} style={{ color: 'var(--c-dim)', marginBottom: 10 }} />
                <div style={{ color: 'var(--c-muted)', fontSize: 13 }}>Votre panier est vide.</div>
                <div style={{ color: 'var(--c-dim)', fontSize: 11, marginTop: 4 }}>Ajoutez des points depuis la carte.</div>
                <button className="pan-btn pan-btn--primary" style={{ marginTop: 14 }} onClick={() => navigate('/expert/maps')}>
                  <MapPin size={13} /> Aller sur la carte
                </button>
              </div>
            ) : items.map(item => (
              <div key={item.id} className="pan-item-card">
                <div style={{
                  width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                  background: item.has_pdf ? '#16a34a' : '#c2410c',
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--c-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.nom}</div>
                  <div style={{ fontSize: 11, color: 'var(--c-muted)', marginTop: 1 }}>
                    {item.lat?.toFixed(5)} / {item.lng?.toFixed(5)}
                    {item.has_pdf && <span style={{ color: '#22c55e', marginLeft: 6 }}>+ PDF</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ color: '#22c55e', fontWeight: 700, fontSize: 13 }}>{item.amount_mga} MGA</div>
                  <div style={{ color: 'var(--c-muted)', fontSize: 10 }}>{item.amount_eur} EUR</div>
                </div>
                <button className="pan-btn pan-btn--danger" style={{ padding: '5px 7px', flexShrink: 0 }} onClick={() => removeFromCart(item.id)}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>

          {items.length > 0 && (
            <div style={{ background: 'rgba(var(--ds-panel-rgb),0.8)', border: '1px solid rgba(var(--ds-indigo-rgb),0.14)', borderRadius: 12, padding: 18, position: 'sticky', top: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Récapitulatif</div>
              {items.map(item => (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--c-muted)', marginBottom: 5 }}>
                  <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.nom}</span>
                  <span>{item.amount_mga} MGA</span>
                </div>
              ))}
              <div style={{ borderTop: '1px solid rgba(var(--ds-indigo-rgb),0.14)', margin: '10px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-text)' }}>Total</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#22c55e' }}>{totalMga} MGA</span>
              </div>
              <div style={{ textAlign: 'right', fontSize: 10, color: 'var(--c-muted)', marginBottom: 14 }}>≈ {totalEur.toFixed(2)} EUR</div>
              <button className="pan-btn pan-btn--primary" style={{ width: '100%', marginBottom: 7 }} onClick={() => setStep('method')}>
                Passer au paiement <ChevronRight size={13} />
              </button>
              <button className="pan-btn pan-btn--ghost" style={{ width: '100%', fontSize: 11 }} onClick={clearCart}>
                <Trash2 size={11} /> Vider le panier
              </button>
            </div>
          )}
        </div>
      )}

      {/* ══ ÉTAPE : MÉTHODE ══ */}
      {step === 'method' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: method === 'stripe' ? '260px 1fr' : '1fr',
          gap: 20, alignItems: 'start', maxWidth: method === 'stripe' ? 860 : 500,
          transition: 'max-width 0.3s ease',
        }}>

          {/* Panneau gauche : choix méthode */}
          <div>
            {/* Récap compact */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'rgba(var(--ds-panel-rgb),0.8)', border: '1px solid rgba(var(--ds-indigo-rgb),0.14)', borderRadius: 8,
              padding: '10px 14px', marginBottom: 14,
            }}>
              <span style={{ color: 'var(--c-muted)', fontSize: 11 }}>{items.length} point{items.length > 1 ? 's' : ''}</span>
              <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 14 }}>{totalMga} MGA</span>
            </div>

            <div style={{ fontSize: 11, color: 'var(--c-muted)', marginBottom: 10 }}>Mode de paiement :</div>

            {METHODS.map(m => {
              const Icon   = m.icon;
              const active = method === m.id;
              return (
                <button key={m.id} className={`pan-method-card${active ? ' pan-method-card--active' : ''}`} onClick={() => setMethod(m.id)}>
                  <div style={{
                    width: 34, height: 34, borderRadius: 7, flexShrink: 0,
                    background: `${m.color}15`, border: `1px solid ${m.color}35`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={15} style={{ color: m.color }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--c-text)' }}>{m.label}</div>
                    <div style={{ fontSize: 10, color: 'var(--c-muted)', marginTop: 1 }}>{m.sub}</div>
                  </div>
                  {m.id !== 'stripe' && (
                    <span style={{
                      fontSize: 9, background: 'rgba(var(--ds-indigo-rgb),0.1)', padding: '1px 5px', borderRadius: 3, flexShrink: 0,
                      color: m.id === 'mvola' ? '#e11d48' : 'var(--c-dim)',
                    }}>
                      {m.id === 'mvola' ? 'test ✓' : 'test'}
                    </span>
                  )}
                  <div style={{
                    width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${active ? m.color : 'var(--c-dim)'}`,
                    background: active ? m.color : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {active && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                </button>
              );
            })}

            {/* Saisie téléphone MoMo / bouton Orange */}
            {method && method !== 'stripe' && (
              <div style={{ marginTop: 14 }}>
                {method !== 'orange' ? (
                  <>
                    <div style={{ fontSize: 11, color: 'var(--c-muted)', marginBottom: 7 }}>
                      Numéro {METHODS.find(m => m.id === method)?.label} :
                    </div>
                    <input
                      className="pan-input" type="tel"
                      placeholder={method === 'airtel' ? '032 XX XXX XX' : '034 XX XXX XX'}
                      value={phone} onChange={e => setPhone(e.target.value)}
                      style={{ marginBottom: 6 }}
                    />
                    <div style={{ fontSize: 10, color: 'var(--c-dim)', marginBottom: 12 }}>Mode test — tout numéro est accepté.</div>
                    <button className="pan-btn pan-btn--primary" style={{ width: '100%' }} onClick={processMomo} disabled={!phone.trim()}>
                      Payer {totalMga} MGA <ChevronRight size={13} />
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{
                      background: 'rgba(249,115,22,0.07)', border: '1px solid rgba(249,115,22,0.2)',
                      borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 11, color: '#fdba74', lineHeight: 1.5,
                    }}>
                      Orange Money WebPay — vous serez redirigé vers la page de paiement Orange.<br />
                      <span style={{ color: 'var(--c-muted)' }}>Mode test : simulation 8 secondes.</span>
                    </div>
                    <button className="pan-btn pan-btn--primary" style={{ width: '100%', background: 'linear-gradient(135deg,#f97316,#ea580c)' }} onClick={processMomo}>
                      Payer {totalMga} MGA via Orange Money <ChevronRight size={13} />
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Panneau droit : formulaire Stripe */}
          {method === 'stripe' && (
            <div style={{ background: 'var(--c-bg)', border: '1px solid rgba(var(--ds-indigo-rgb),0.14)', borderRadius: 12, padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#4f46e5', letterSpacing: 0.5, marginBottom: 14, textTransform: 'uppercase' }}>
                Paiement sécurisé
              </div>
              <StripeForm
                onPaymentMethod={processStripe}
                disabled={false}
                totalMga={totalMga}
                totalEur={totalEur}
                itemCount={items.length}
              />
            </div>
          )}
        </div>
      )}

      {/* ══ ÉTAPE : TRAITEMENT ══ */}
      {step === 'pay' && (
        <div style={{ maxWidth: 460 }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ background: 'rgba(var(--ds-indigo-rgb),0.18)', borderRadius: 999, height: 5, marginBottom: 6, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 999, background: '#4f46e5',
                width: `${(progress.current / Math.max(progress.total, 1)) * 100}%`,
                transition: 'width 0.4s ease',
              }} />
            </div>
            <div style={{ fontSize: 11, color: 'var(--c-muted)', textAlign: 'right' }}>{progress.current} / {progress.total}</div>
          </div>

          {items.map((item, idx) => {
            const done   = progress.results.find(r => r.id === item.id);
            const active = !done && idx + 1 === progress.current;
            return (
              <div key={item.id} className="pan-item-card" style={{
                opacity: (!done && !active) ? 0.35 : 1,
                borderColor: done ? (done.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)') : 'rgba(var(--ds-indigo-rgb),0.14)',
              }}>
                {done
                  ? done.ok
                    ? <CheckCircle size={15} style={{ color: '#22c55e', flexShrink: 0 }} />
                    : <AlertCircle size={15} style={{ color: '#ef4444', flexShrink: 0 }} />
                  : active
                    ? <Loader size={15} style={{ color: '#818cf8', animation: 'spin 0.65s linear infinite', flexShrink: 0 }} />
                    : <Lock size={13} style={{ color: 'var(--c-dim)', flexShrink: 0 }} />
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--c-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.nom}</div>
                  {active && (method === 'mvola' || method === 'airtel') && (
                    <div style={{ fontSize: 10, color: '#818cf8', marginTop: 1 }}>
                      Acceptez le push USSD sur {phone} …
                    </div>
                  )}
                  {active && method === 'orange' && (
                    <div style={{ fontSize: 10, color: '#f97316', marginTop: 1 }}>
                      Page Orange Money ouverte — finalisez le paiement…
                    </div>
                  )}
                  {done && !done.ok && <div style={{ fontSize: 10, color: '#f87171', marginTop: 1 }}>{done.err}</div>}
                </div>
                <div style={{ color: '#22c55e', fontWeight: 600, fontSize: 12, flexShrink: 0 }}>{item.amount_mga} MGA</div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══ ÉTAPE : RÉSULTATS ══ */}
      {step === 'done' && (() => {
        const ok     = progress.results.filter(r => r.ok);
        const failed = progress.results.filter(r => !r.ok);
        const allOk  = failed.length === 0;
        return (
          <div style={{ maxWidth: 460 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', margin: '0 auto 18px',
              background: allOk ? 'rgba(34,197,94,0.1)' : 'rgba(234,179,8,0.1)',
              border: `2px solid ${allOk ? '#22c55e' : '#eab308'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <CheckCircle size={26} style={{ color: allOk ? '#22c55e' : '#eab308' }} />
            </div>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--c-text)', marginBottom: 5 }}>
                {allOk ? 'Tous les paiements réussis !' : `${ok.length} / ${progress.results.length} réussis`}
              </div>
              <div style={{ fontSize: 12, color: 'var(--c-muted)' }}>
                {allOk ? 'Coordonnées Laborde accessibles depuis la carte et "Mes Points".' : 'Certains paiements ont échoué.'}
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              {progress.results.map(r => (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 9,
                  padding: '8px 11px', borderRadius: 7, marginBottom: 5,
                  background: r.ok ? 'rgba(34,197,94,0.05)' : 'rgba(239,68,68,0.05)',
                  border: `1px solid ${r.ok ? 'rgba(34,197,94,0.18)' : 'rgba(239,68,68,0.18)'}`,
                }}>
                  {r.ok
                    ? <CheckCircle size={13} style={{ color: '#22c55e', flexShrink: 0 }} />
                    : <AlertCircle size={13} style={{ color: '#ef4444', flexShrink: 0 }} />
                  }
                  <span style={{ flex: 1, fontSize: 12, color: 'var(--c-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.nom}</span>
                  {r.ok
                    ? <span style={{ fontSize: 10, color: '#22c55e', fontWeight: 600, flexShrink: 0 }}>Déverrouillé</span>
                    : <span style={{ fontSize: 10, color: '#f87171', flexShrink: 0 }}>{r.err}</span>
                  }
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 9 }}>
              <button className="pan-btn pan-btn--primary" style={{ flex: 1 }} onClick={() => navigate('/expert/maps')}>
                <MapPin size={13} /> Voir la carte
              </button>
              <button className="pan-btn pan-btn--ghost" style={{ flex: 1 }} onClick={() => navigate('/expert/mes-points')}>
                <FileDown size={13} /> Mes Points
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
